import { verifiedFetch } from '~/utils/requestVerification'
import {
  fetchWithTimeout,
  withUploadRetry,
} from '~/utils/uploadRetry.js'

/** 图片一次性上传上限；超过则走分片（与 chunkUploader.CHUNK_SIZE_BYTES 一致） */
export const SIMPLE_UPLOAD_MAX_BYTES = 5 * 1024 * 1024

/**
 * 从 File 推导 OSS 后缀，对齐调用方：file.type.split("/")[1]
 * 无 MIME 时回退文件扩展名。
 */
export function resolveOssFileSuffix(file) {
  const mimePart = String(file?.type || '').split('/')[1]
  if (mimePart) return mimePart
  const name = String(file?.name || '')
  const ext = name.includes('.') ? name.split('.').pop() : ''
  return String(ext || 'bin').trim() || 'bin'
}

export function isImageUploadFile(file) {
  const type = String(file?.type || '').toLowerCase()
  if (type.startsWith('image/')) return true
  return /\.(jpe?g|png|webp|gif|bmp|heic|heif)$/i.test(String(file?.name || ''))
}

function createOssUploadError(uploadStage, failReason, message, cause, extras = {}) {
  const error = new Error(message || failReason || 'upload failed')
  error.uploadStage = uploadStage
  error.failReason = failReason
  if (cause) error.cause = cause
  if (extras.httpStatus != null) error.httpStatus = Number(extras.httpStatus)
  if (extras.businessCode != null) error.businessCode = Number(extras.businessCode)
  return error
}

function failReasonForHttpStatus(status, fallback = 'api_error') {
  const code = Number(status)
  if (code === 401 || code === 403) return 'auth_required'
  return fallback
}

/**
 * 一次性签名上传（适合小文件，尤其图片 ≤ 5MB）。
 * 预签名与每个 PUT 各自最多 3 次尝试（失败只重试当前步骤）。
 * @returns {Promise<string|string[]>} 单个文件返回 get URL；数组返回 URL 列表
 */
export async function uploadFileToOSS(file, f_suffixs) {
  const files = Array.isArray(file) ? file : [file]
  if (!files.length || files.some((f) => !f)) {
    throw createOssUploadError('pre_sign', 'invalid_param', 'file is required')
  }

  const safeSuffixes = Array.isArray(f_suffixs)
    ? f_suffixs.map((s) => String(s || '').trim()).filter(Boolean)
    : f_suffixs != null && String(f_suffixs).trim()
      ? [String(f_suffixs).trim()]
      : files.map((f) => resolveOssFileSuffix(f))

  if (safeSuffixes.length < files.length) {
    throw createOssUploadError(
      'pre_sign',
      'invalid_param',
      'f_suffixs length must cover all files',
    )
  }

  const signedUrls = await withUploadRetry(async () => {
    let response
    try {
      // _signRetry: 禁用 verifiedFetch 对 100060 的零延迟内置重试，改由 withUploadRetry 统一退避
      response = await fetchWithTimeout(async (signal) =>
        verifiedFetch('/api/aigc/pre_interface/get_oss_signed_urls', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            f_suffixs: safeSuffixes.slice(0, files.length),
          }),
          _signRetry: true,
          ...(signal ? { signal } : {}),
        }),
      )
    } catch (error) {
      if (error?.failReason === 'timeout' || error?.name === 'TimeoutError') {
        throw createOssUploadError('pre_sign', 'timeout', error.message, error)
      }
      throw createOssUploadError(
        'pre_sign',
        'network_error',
        error?.message || 'Failed to get OSS URLs',
        error,
      )
    }

    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw createOssUploadError(
        'pre_sign',
        failReasonForHttpStatus(response.status, 'api_error'),
        `Failed to get OSS URLs: ${response.status} ${text}`,
        null,
        { httpStatus: response.status },
      )
    }

    const res_oss = await response.json()
    const businessCode = Number(res_oss?.code)
    if (Number.isFinite(businessCode) && businessCode !== 100000) {
      const failReason = businessCode === 100060 ? 'sign_failed' : 'api_error'
      throw createOssUploadError(
        'pre_sign',
        failReason,
        res_oss?.message || `Failed to get OSS URLs (code ${businessCode})`,
        null,
        { businessCode },
      )
    }

    const urls = res_oss?.data?.oss_signed_urls
    if (!Array.isArray(urls) || !urls.length) {
      throw createOssUploadError(
        'pre_sign',
        'sign_failed',
        res_oss?.message || 'Failed to get OSS URLs',
      )
    }
    if (urls.length < files.length) {
      throw createOssUploadError(
        'pre_sign',
        'sign_failed',
        `Not enough signed URLs: expect ${files.length}, got ${urls.length}`,
      )
    }
    return urls
  })

  const results = []
  for (let i = 0; i < files.length; i++) {
    const item = signedUrls[i]
    const putUrl = item?.put
    const getUrl = item?.get
    if (!putUrl || !getUrl) {
      throw createOssUploadError(
        'pre_sign',
        'sign_failed',
        `Invalid signed URL payload at index ${i}`,
      )
    }

    await withUploadRetry(async () => {
      let putResponse
      try {
        // 不主动带 Content-Type：签名 URL 常对 header 敏感，与历史一次性上传行为一致
        putResponse = await fetch(putUrl, {
          method: 'PUT',
          body: files[i],
        })
      } catch (error) {
        throw createOssUploadError(
          'chunk_upload',
          'network_error',
          error?.message || 'Upload failed',
          error,
        )
      }

      if (!putResponse.ok) {
        throw createOssUploadError(
          'chunk_upload',
          failReasonForHttpStatus(putResponse.status, 'chunk_upload_failed'),
          files.length > 1
            ? `Upload failed for file at index ${i}`
            : 'Upload failed',
          null,
          { httpStatus: putResponse.status },
        )
      }
    })

    results.push(getUrl)
  }

  return Array.isArray(file) ? results : results[0]
}
